// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import * as activitiesApi from "../api"

//Tiempo de vida del catálogo de actividades en el store (30 minutos)
export const ACTIVITIES_CACHE_TTL = 30 * 60 * 1000

const asList = (data) => (Array.isArray(data) ? data : [])

const isLatest = (state, action) => state.requestId === action.meta.requestId

export const getActivities = createAsyncThunk('appActivities/getActivities', async (_, { rejectWithValue }) => {
    try {
        return {
            data: await activitiesApi.fetchBusinessActivities(),
            fetchedAt: Date.now()
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
}, {
    //Evita peticiones duplicadas y reutiliza el caché mientras siga vigente
    condition: (_, { getState }) => {
        const activities = getState()?.activities

        if (!activities) return true
        //Invalidación explícita (enableRequest): pasa aunque haya una petición en vuelo,
        //que pudo salir antes de la escritura; su respuesta se descarta por requestId
        if (activities.isRequested) return true
        //Ya hay una petición en vuelo: los demás se cuelgan de esa
        if (activities.status === 'pending') return false
        //Store vacío
        if (!activities.data?.length) return true

        return Date.now() - (activities.lastFetch ?? 0) > ACTIVITIES_CACHE_TTL
    }
})

export const appActivitiesSlice = createSlice({
    name: 'appActivities',
    initialState: {
        data: [],
        isRequested: true,
        status: 'idle',
        error: null,
        lastFetch: null,
        requestId: null
    },
    reducers: {
        enableRequest: (state) => {
            state.isRequested = true
        }
    },
    extraReducers: builder => {
        builder.addCase(getActivities.pending, (state, action) => {
            state.status = 'pending'
            state.error = null
            //Se consume aquí para que una invalidación durante la petición siga vigente
            state.isRequested = false
            state.requestId = action.meta.requestId
        })
        builder.addCase(getActivities.fulfilled, (state, action) => {
            if (!isLatest(state, action)) return
            state.status = 'fulfilled'
            state.data = asList(action.payload.data)
            state.lastFetch = action.payload.fetchedAt
        })
        builder.addCase(getActivities.rejected, (state, action) => {
            if (!isLatest(state, action)) return
            state.status = 'rejected'
            state.error = action.payload ?? action.error?.message ?? null
            //Permite reintentar en la siguiente llamada sin esperar al TTL
            state.isRequested = true
        })
    }
})

export const { enableRequest } = appActivitiesSlice.actions

//La recarga no se espera: el alta/edición se resuelve en cuanto responde el backend y
//la lista nueva llega después con `status: 'pending'` mientras tanto
export const addActivity = createAsyncThunk('appActivities/addActivity', async (activity, { dispatch, rejectWithValue }) => {
    try {
        const data = await activitiesApi.createBusinessActivity(activity)
        dispatch(enableRequest())
        dispatch(getActivities())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const updateActivity = createAsyncThunk('appActivities/updateActivity', async (payload, { dispatch, rejectWithValue }) => {
    try {
        const data = await activitiesApi.updateBusinessActivity(payload.id, payload.activity)
        dispatch(enableRequest())
        dispatch(getActivities())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export default appActivitiesSlice.reducer
